import { useEffect, useMemo, useState } from "react";
import { PageHeader, StatCard, SectionCard, Pill } from "@/components/ui-bits";
import { FileQuestion, ClipboardList, GraduationCap, Trophy, Plus, Clock, User2, ArrowRight, Sparkles, Share2, Users } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { labelFor } from "@/lib/constants";
import ShareAssessmentModal from "@/components/ShareAssessmentModal";

const NEW_WINDOW_MS = 24 * 60 * 60 * 1000;   // "NEW" badge for tests created in the last 24h

function timeAgo(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = Date.now() - t;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} min ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} h ago`;
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)} d ago`;
  return iso.slice(0, 10);
}

export default function TrainerDashboard() {
  const [stats, setStats] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [recent, setRecent] = useState([]);
  const [sharing, setSharing] = useState(null);         // assessment we're currently sharing
  const [shareInfo, setShareInfo] = useState({});       // {assessment_id: {user_count, effective_reach}}

  const loadShareCounts = async (list) => {
    // Fetch share reach for the first N assessments so we can show a badge on each card
    const entries = await Promise.all(list.slice(0, 6).map(async (a) => {
      try {
        const { data } = await api.get(`/assessments/${a.id}/share`);
        return [a.id, data];
      } catch {
        return [a.id, null];
      }
    }));
    setShareInfo(Object.fromEntries(entries.filter(([, v]) => v)));
  };

  useEffect(() => {
    api.get("/stats/overview").then(r => setStats(r.data));
    api.get("/assessments").then((r) => {
      setAssessments(r.data);
      loadShareCounts(r.data);
    });
    api.get("/results").then(r => setRecent(r.data.slice(0, 6)));
  }, []);

  const refreshShareInfo = async (aid) => {
    try {
      const { data } = await api.get(`/assessments/${aid}/share`);
      setShareInfo((s) => ({ ...s, [aid]: data }));
    } catch { /* noop */ }
  };

  // Newest tests (backend already sorts desc by created_at, take top 6)
  const topAssessments = useMemo(() => assessments.slice(0, 6), [assessments]);
  const totalAssessments = assessments.length;

  return (
    <div>
      <PageHeader
        title="Trainer Hub"
        subtitle="Build question banks, schedule assessments, and review learner outcomes."
        actions={
          <Link to="/trainer/create-test" data-testid="quick-create-test"
            className="inline-flex items-center gap-2 bg-blue-600 text-white rounded-lg px-4 py-2 font-medium hover:bg-blue-700">
            <Plus className="w-4 h-4" /> New assessment
          </Link>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Questions" value={stats?.questions ?? "—"} icon={FileQuestion} accent="blue" testId="trainer-stat-questions" />
        <StatCard label="Assessments" value={stats?.assessments ?? "—"} icon={ClipboardList} accent="purple" testId="trainer-stat-assessments" />
        <StatCard label="Employees" value={stats?.students ?? "—"} icon={GraduationCap} accent="amber" testId="trainer-stat-students" />
        <StatCard label="Pass rate" value={`${stats?.pass_rate ?? 0}%`} icon={Trophy} accent="green" testId="trainer-stat-pass-rate" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        {/* Recently created tests — the key surface Manager / TL / Trainer all land on */}
        <div className="lg:col-span-2">
          <SectionCard
            title={
              <div className="flex items-center gap-2">
                <ClipboardList className="w-4 h-4 text-blue-600" />
                <span>Recently created tests</span>
                <span className="text-xs text-slate-400 font-normal ml-1">({totalAssessments} total)</span>
              </div>
            }
            action={
              <Link to="/trainer/create-test"
                className="text-xs text-blue-600 hover:text-blue-700 inline-flex items-center gap-1"
                data-testid="dashboard-view-all">
                View all <ArrowRight className="w-3 h-3" />
              </Link>
            }
          >
            {topAssessments.length === 0 ? (
              <div className="py-10 text-center">
                <div className="w-12 h-12 rounded-full bg-blue-50 grid place-items-center mx-auto mb-3">
                  <ClipboardList className="w-6 h-6 text-blue-600" />
                </div>
                <div className="text-sm font-medium text-slate-700">No tests yet</div>
                <div className="text-xs text-slate-500 mt-0.5">Create your first assessment to see it here.</div>
                <Link to="/trainer/create-test"
                  className="mt-4 inline-flex items-center gap-1.5 bg-blue-600 text-white rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-blue-700">
                  <Plus className="w-4 h-4" /> New assessment
                </Link>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100 -mt-1">
                {topAssessments.map((a) => {
                  const isNew = a.created_at && (Date.now() - new Date(a.created_at).getTime()) < NEW_WINDOW_MS;
                  const share = shareInfo[a.id];
                  const shareReach = share?.effective_reach || 0;
                  return (
                    <li key={a.id} data-testid={`dashboard-test-${a.id}`}
                      className="py-3 flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-slate-900">{a.title}</span>
                          {isNew && (
                            <span data-testid={`dashboard-test-new-${a.id}`}
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5">
                              <Sparkles className="w-2.5 h-2.5" /> New
                            </span>
                          )}
                          {a.assessment_type && (
                            <Pill tone="indigo">{labelFor("assessment_types", a.assessment_type)}</Pill>
                          )}
                          {a.level && (
                            <Pill tone="blue">{labelFor("assessment_levels", a.level)}</Pill>
                          )}
                          {shareReach > 0 && (
                            <span data-testid={`dashboard-test-shared-${a.id}`}
                              className="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-1.5 py-0.5">
                              <Users className="w-2.5 h-2.5" /> {shareReach} shared
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-xs text-slate-500 flex items-center gap-3 flex-wrap">
                          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{a.duration} min · {a.question_ids?.length || 0} questions · Pass ≥ {a.passing_marks}%</span>
                        </div>
                        <div className="mt-1 text-xs text-slate-400 flex items-center gap-2 flex-wrap">
                          {a.created_by_name && (
                            <span className="inline-flex items-center gap-1"><User2 className="w-3 h-3" />by {a.created_by_name}</span>
                          )}
                          <span>·</span>
                          <span>{timeAgo(a.created_at)}</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSharing(a)}
                        data-testid={`dashboard-share-${a.id}`}
                        className="inline-flex items-center gap-1.5 text-xs font-medium border border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg px-2.5 py-1.5 flex-shrink-0">
                        <Share2 className="w-3.5 h-3.5" /> Share
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </SectionCard>
        </div>

        <SectionCard title="Recent submissions">
          {recent.length === 0 ? <div className="text-sm text-slate-500 py-6 text-center">No submissions yet.</div> : (
            <ul className="space-y-3">
              {recent.map(r => (
                <li key={r.id} className="flex items-start justify-between gap-3 border-b border-slate-100 pb-3 last:border-0">
                  <div>
                    <div className="text-sm font-medium text-slate-900">{r.student_name}</div>
                    <div className="text-xs text-slate-500">{r.assessment_title}</div>
                  </div>
                  <Pill tone={r.status === "passed" ? "green" : "red"}>{r.percentage}%</Pill>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>

      {sharing && (
        <ShareAssessmentModal
          assessment={sharing}
          onClose={() => setSharing(null)}
          onSaved={() => refreshShareInfo(sharing.id)}
        />
      )}
    </div>
  );
}
